import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  marketingColor,
  marketingEyebrow,
  marketingFont,
  marketingLine,
} from '../../theme/marketingTokens';

/**
 * Marketing photography.
 *
 * The handoff ships drag-and-drop placeholders rather than final assets, and
 * notes that real Sony a7 IV imagery and partner logos are still outstanding.
 * `src: undefined` means "not supplied yet" — the slot then draws its own
 * framed placeholder instead of requesting a missing file, so an unfilled slot
 * costs no 404 and still reads as intentional.
 */
export const marketingImages = {
  /** Product dashboard screenshot behind the hero. */
  heroDashboard: '/marketing/hero-dashboard.png' as string | undefined,
  /**
   * Travels the pipeline scene and lands as the storefront listing's photo.
   *
   * Needs a transparent (or #0A1418) ground: it sits on the ink stage with no
   * plate behind it, so a white background reads as a bright box. Rendered
   * into square boxes with `objectFit: contain`, so a square source avoids
   * letterboxing.
   */
  pipelineCamera: '/marketing/pipeline-camera.png' as string | undefined,
  /** Dark camera mark shown until real photography is supplied. */
  pipelineCameraFallback: '/marketing/pipeline-camera.svg' as string | undefined,

  /**
   * Leadership headshots on /about, keyed by the member `id` in the locale
   * files rather than by array position, so reordering the team cannot put
   * someone else's face on a card.
   *
   * A missing or broken headshot falls back to the member's initials, which
   * is what the card drew before photos existed.
   *
   * All lowercase `.jpg` on purpose. Firebase Hosting serves these paths
   * case-sensitively, so an upload named `gary.JPG` would not answer a
   * request for `gary.jpg`; normalising at commit time removes a footgun
   * that is invisible on a case-insensitive Mac and only shows up in
   * production.
   *
   * tracey.png is a PNG where the other two are JPEGs — that is simply what
   * was uploaded, and the path has to match byte for byte.
   */
  team: {
    tracey: '/marketing/tracey.png',
    dustin: '/marketing/dustin.jpg',
    gary: '/marketing/gary.jpg',
  } as Record<string, string | undefined>,
};

interface MarketingImageSlotProps {
  src?: string;
  /** Tried when `src` is absent or fails — e.g. a placeholder mark behind real photography. */
  fallbackSrc?: string;
  alt: string;
  /** Shown inside the frame when no image resolves at all. */
  placeholder: string;
  sx?: object;
  imageSx?: object;
}

export const MarketingImageSlot: React.FC<MarketingImageSlotProps> = ({
  src,
  fallbackSrc,
  alt,
  placeholder,
  sx,
  imageSx,
}) => {
  const [failedSrc, setFailedSrc] = useState<string | undefined>();
  const primary = src && src !== failedSrc ? src : undefined;
  const resolved = primary ?? (fallbackSrc !== failedSrc ? fallbackSrc : undefined);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: marketingColor.ink,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {resolved ? (
        <Box
          component="img"
          src={resolved}
          alt={alt}
          onError={() => setFailedSrc(resolved)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', ...imageSx }}
        />
      ) : (
        <Typography
          component="span"
          sx={{
            fontFamily: marketingFont.mono,
            fontSize: 11,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: marketingColor.textMuted,
            textAlign: 'center',
            px: 2,
          }}
        >
          {placeholder}
        </Typography>
      )}
    </Box>
  );
};

/** `// lowercase` mono label in a hairline box. */
export const Eyebrow: React.FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx,
}) => (
  <Box component="span" sx={{ ...marketingEyebrow, ...sx }}>
    {children}
  </Box>
);

/** Primary marketing CTA — accent fill, ink label. */
export const ctaButtonSx = {
  border: `1px solid ${marketingColor.accent}`,
  backgroundColor: marketingColor.accent,
  color: marketingColor.ink,
  fontFamily: marketingFont.display,
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  px: 3.5,
  py: 1.9,
  borderRadius: '3px',
  '&:hover': { backgroundColor: marketingColor.accentHover },
} as const;

/** Secondary marketing CTA — hairline outline, accent label. */
export const ghostButtonSx = {
  border: `1px solid ${marketingLine.hairlineStrong}`,
  backgroundColor: 'transparent',
  color: marketingColor.accent,
  fontFamily: marketingFont.display,
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  px: 3.5,
  py: 1.9,
  borderRadius: '3px',
  '&:hover': {
    borderColor: marketingColor.accent,
    backgroundColor: 'rgba(95,226,138,0.12)',
  },
} as const;
